import {useEffect, useRef, useState} from 'react';
import {useResizeObserver} from '@pb/kit-hooks-use-resize-observer';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import {getScrollMetrics} from '../utils/get-scroll-metrics';

const SCROLL_STEP = 5;

export interface UseScrollNavigationOptions {
    /**
     * When `false`, arrow visibility tracking is disabled and both
     * `isAtScrollStart` / `isAtScrollEnd` are always `true`. Defaults to `true`.
     */
    enabled?: boolean,
    /**
     * When `true`, tracks vertical (`scrollTop`) overflow; when `false`
     * (default), tracks horizontal (`scrollLeft`) overflow.
     */
    isVertical?: boolean,
}

export interface UseScrollNavigationReturn {
    /**
     * Attach to the element that has `overflow: auto/scroll`. The hook reads
     * `scrollLeft` / `scrollTop` from this element to determine arrow visibility.
     */
    scrollRef: React.RefObject<HTMLDivElement | null>,
    /**
     * Attach to the direct flex/grid container whose children are the scrollable
     * items. A `ResizeObserver` watches this element to recalculate boundaries
     * when the item count or sizes change.
     */
    containerRef: React.RefObject<HTMLElement | null>,
    /**
     * `true` when the scroll position is at (or within a 5 px tolerance of)
     * the start — used to hide the "previous" arrow.
     */
    isAtScrollStart: boolean,
    /**
     * `true` when the scroll position is at (or within a 5 px tolerance of)
     * the end — used to hide the "next" arrow.
     */
    isAtScrollEnd: boolean,
    /**
     * Recalculates `isAtScrollStart` and `isAtScrollEnd` from the current
     * scroll position. Pass `forceUpdate = true` to bypass the 5 px debounce
     * threshold (e.g. after a programmatic scroll or item count change).
     */
    updateScrollBoundaries: (forceUpdate?: boolean) => void,
}

/**
 * Manages the visibility state of scroll-navigation arrows for a horizontally
 * or vertically overflowing container.
 *
 * Attach `scrollRef` to the element that scrolls and `containerRef` to the
 * element whose children are items. The hook tracks whether the scroll position
 * is at the start or end and exposes `isAtScrollStart` / `isAtScrollEnd`
 * booleans that control arrow rendering in `<SlideArrows>`.
 *
 * @param options - Hook configuration
 * @returns Refs and scroll-boundary state
 */
export const useScrollNavigation = (options: UseScrollNavigationOptions = {}): UseScrollNavigationReturn => {
    const {
        enabled = true,
        isVertical = false,
    } = options;

    const [isAtScrollStart, setIsAtScrollStart] = useState<boolean>(true);
    const [isAtScrollEnd, setIsAtScrollEnd] = useState<boolean>(true);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLElement | null>(null);
    const prevScrollPosition = useRef<number>(0);

    const [listWidth, listHeight] = useResizeObserver(containerRef as React.RefObject<HTMLElement>);

    const updateScrollBoundaries = useCallbackWithRef((forceUpdate: boolean = false) => {
        if (!scrollRef.current) {
            return;
        }

        const {scrollPosition, viewportSize, contentSize} = getScrollMetrics(
            scrollRef.current,
            isVertical,
        );

        if (!forceUpdate && Math.abs(scrollPosition - prevScrollPosition.current) <= SCROLL_STEP) {
            return;
        }
        prevScrollPosition.current = scrollPosition;

        const atStart = scrollPosition <= SCROLL_STEP;
        const atEnd = Math.abs(scrollPosition + viewportSize - contentSize) <= SCROLL_STEP;

        setIsAtScrollStart(atStart);
        setIsAtScrollEnd(atEnd);
    });

    const relevantSize = isVertical ? listHeight : listWidth;

    useEffect(() => {
        if (enabled) {
            updateScrollBoundaries(true);
        } else {
            setIsAtScrollStart(true);
            setIsAtScrollEnd(true);
        }
    }, [relevantSize, enabled, updateScrollBoundaries]);

    return {
        scrollRef,
        containerRef,
        isAtScrollStart,
        isAtScrollEnd,
        updateScrollBoundaries,
    };
};
